var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { EventEmitter, OnInit, AfterViewInit, Renderer2, ChangeDetectorRef, OnDestroy } from '@angular/core';
import * as Hammer from 'hammerjs';
import { Select, Store } from '@ngxs/store';
import { Subject, Observable } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { ViewState } from '../../state/view.state';
import { ViewMode } from '../../models/view-mode.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ViewService } from '../../view.service';
import { ViewToolsMode } from '../../models/view-tools-mode.enum';
const drawerHeight = 64;
const velocityMultipler = 50; // Higher is faster
const triggerVelocityThreshold = 0.2; // Velocity at which drawer is considered 'in motion'. Higher is harder.
const snapVelocity = 0.75; // Velocity at which drawer auto animates. 
const friction = 0.92; // Lower is more aggressive
const toggleSpeed = 0.75; // Speed at which drawer is opened by click
const halfwayPoint = 0.4;
export class ViewDrawerComponent {
    // ---------- Lifecycle ----------
    constructor(renderer, changeDetectorRef, viewService, store) {
        this.renderer = renderer;
        this.changeDetectorRef = changeDetectorRef;
        this.viewService = viewService;
        this.store = store;
        // ---------- Input / Output ----------
        this.enablePagination = true;
        this._expandable = true;
        this.offsetYChange = new EventEmitter();
        this.breakpoints = {
            minimumDrawer: {
                velocity: Infinity,
                point: 0
            },
            halfDrawer: {
                velocity: 1,
                point: 0
            },
            fullWindow: {
                velocity: Infinity,
                point: 0
            },
            fullDrawer: {
                velocity: Infinity,
                point: 0
            }
        };
        this.canTouch = false;
        this.ngUnsubscribe = new Subject();
        this.viewMode = ViewMode;
        this.viewToolsMode = ViewToolsMode;
        this.ghostPagination = false;
        this.atMinimum = false;
        this.mc = null;
        this.mode = ViewMode.Content;
        this.hovered = false;
    }
    get expandable() {
        return this._expandable;
    }
    set expandable(value) {
        this._expandable = value;
        (this.expandable) ? this.bindDragEvents() : this.unbindDragEvents();
    }
    // ---------- Class Variables ----------
    get fullyOpen() {
        if (this.canTouch) {
            return this.getDrawerY() <= this.breakpoints.fullWindow.point;
        }
        else {
            return Math.floor(this.getDrawerY()) === Math.floor(this.breakpoints.halfDrawer.point);
        }
    }
    ngOnInit() {
        this.initWindowSizeObserver();
        this.initLayoutObserver();
    }
    ngAfterViewInit() {
        this.initInteractions();
        this.refresh(this.store.selectSnapshot(ViewState.getLayout).drawer.isExpandedInitially);
    }
    ngOnDestroy() {
        this.unbindDragEvents();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    // ---------- Layout ----------
    initWindowSizeObserver() {
        this.renderer.listen('window', 'resize', () => {
            this.refresh(false);
        });
    }
    initLayoutObserver() {
        this.layout$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(layout => {
            this.updateMode(layout);
        });
    }
    /**
     * Mobile devices have variable viewheight because the browser bar can scroll in and out of view
     * This workaround determines the available window space and uses that as a viewheight unit
     */
    updateViewHeight() {
        const vh = window.innerHeight * 0.01;
        this.viewDrawer.nativeElement.style.setProperty('--vh', `${vh}px`);
    }
    updateStops() {
        const windowHeight = window.innerHeight;
        this.breakpoints.minimumDrawer.point = -drawerHeight;
        this.breakpoints.halfDrawer.point = Math.floor(-(windowHeight * halfwayPoint));
        this.breakpoints.fullWindow.point = -windowHeight;
        this.breakpoints.fullDrawer.point = -this.viewDrawer.nativeElement.offsetHeight;
    }
    updateMode(layout) {
        this.mode = this.viewService.inferDrawerMode(layout);
        if (this.mode === ViewMode.Tools) {
            this.toggleDrawer(false);
        }
        this.changeDetectorRef.markForCheck();
    }
    resetDrawerPosition(expanded) {
        setTimeout(() => {
            const point = expanded
                ? this.breakpoints.halfDrawer.point
                : this.breakpoints.minimumDrawer.point;
            this.flyTo(point);
        });
    }
    refresh(expanded) {
        setTimeout(() => {
            this.updateViewHeight();
            this.updateStops();
            this.resetDrawerPosition(expanded);
        });
    }
    // ---------- Interaction ----------
    initInteractions() {
        this.canTouch$.pipe(filter(canTouch => canTouch), take(1)).subscribe(() => {
            this.canTouch = true;
            this.changeDetectorRef.markForCheck();
            if (this.expandable) {
                this.bindDragEvents();
            }
        });
    }
    bindDragEvents() {
        if (!this.viewDrawer) {
            return;
        }
        // Clear out previous handlers
        this.unbindDragEvents();
        const element = this.viewDrawer.nativeElement;
        this.mc = new Hammer(element);
        this.mc.get('pan').set({ direction: Hammer.DIRECTION_VERTICAL, threshold: 5 });
        this.mc.on('panstart', () => {
            this.handlePanStart();
        });
        this.mc.on('pan', (event) => {
            this.handlePan(event);
        });
        this.mc.on('panend', (event) => {
            this.updateStops(); // Ensure the scroll velocity always works properly
            this.handlePanEnd(event);
        });
    }
    unbindDragEvents() {
        if (this.mc) {
            this.mc.destroy();
        }
    }
    handlePanStart() {
        this.drawerY = this.getDrawerY();
    }
    handlePan(event) {
        this.flyTo(this.drawerY + event.deltaY);
    }
    handlePanEnd(event) {
        this.moveDrawerWithVelocity(event.velocityY);
    }
    handleNextChapter() {
        this.viewService.nextChapter();
    }
    handlePreviousChapter() {
        this.viewService.previousChapter();
    }
    handleToggleClick() {
        this.toggleDrawer();
    }
    // ---------- Utility ----------
    toggleDrawer(open) {
        if (open != null) {
            const breakpoint = open ? this.breakpoints.halfDrawer : this.breakpoints.minimumDrawer;
            const velocity = open ? -toggleSpeed : toggleSpeed;
            this.moveDrawerToBreakpoint(breakpoint, velocity);
        }
        else {
            const breakpoint = this.fullyOpen ? this.breakpoints.minimumDrawer : this.breakpoints.halfDrawer;
            const velocity = breakpoint.point < this.getDrawerY() ? -toggleSpeed : toggleSpeed;
            this.moveDrawerToBreakpoint(breakpoint, velocity);
        }
    }
    moveDrawerWithVelocity(velocityY) {
        const drawerY = this.getDrawerY();
        // If drawer is in motion, get next on it's path. Otherwise calculate nearest. 
        const breakpoint = (Math.abs(velocityY) > triggerVelocityThreshold) ?
            this.getNextBreakpoint(drawerY, velocityY) :
            this.getNearestBreakpoint(drawerY);
        // Ensure a velocity when there is none
        if (breakpoint != null && Math.abs(velocityY) < snapVelocity && !this.fullyOpen) {
            const direction = breakpoint.point < drawerY ? -1 : 1;
            velocityY = snapVelocity * direction;
        }
        // Move drawer to chosen breakpoint.
        if (breakpoint != null) {
            this.moveDrawerToBreakpoint(breakpoint, velocityY);
        }
    }
    getNextBreakpoint(drawerY, velocity) {
        let breakpoints = Object.values(this.breakpoints);
        const absoluteVelocity = Math.abs(velocity);
        if (velocity < 0) {
            for (const breakpoint of breakpoints) {
                if (breakpoint.point < drawerY && absoluteVelocity < breakpoint.velocity) {
                    return breakpoint;
                }
            }
        }
        else {
            breakpoints = breakpoints.reverse();
            for (const breakpoint of breakpoints) {
                if (breakpoint.point > drawerY && absoluteVelocity < breakpoint.velocity) {
                    return breakpoint;
                }
            }
        }
    }
    getNearestBreakpoint(drawerY, breakpoints) {
        let nearestBreakpoint;
        let minimumDiff = Infinity;
        if (breakpoints == null) {
            breakpoints = Object.values(this.breakpoints);
        }
        breakpoints
            .forEach(breakpoint => {
            const diff = Math.abs(Math.abs(breakpoint.point) - Math.abs(drawerY));
            if (diff < minimumDiff) {
                minimumDiff = diff;
                nearestBreakpoint = breakpoint;
            }
        });
        return nearestBreakpoint;
    }
    moveDrawerToBreakpoint(breakpoint, velocityY) {
        const distance = velocityY * velocityMultipler;
        requestAnimationFrame(() => {
            const drawerY = this.getDrawerY();
            // Drawer not active, do not keep attempting to move it as this will cause
            // an infinite loop
            if (typeof drawerY === 'undefined') {
                return;
            }
            // Check if drawer should snap to breakpoint
            const shouldSnapDown = velocityY < 0 && (drawerY + distance) <= breakpoint.point;
            const shouldSnapUp = velocityY > 0 && (drawerY + distance) >= breakpoint.point;
            if (shouldSnapDown || shouldSnapUp) {
                this.flyTo(breakpoint.point);
                // Otherwise, keep moving
            }
            else {
                // Apply friction
                if (this.fullyOpen) {
                    velocityY = velocityY * friction;
                }
                const targetY = drawerY + (velocityY * velocityMultipler);
                this.flyTo(targetY);
                if (Math.abs(velocityY) > 0.05) {
                    this.moveDrawerToBreakpoint(breakpoint, velocityY);
                }
            }
        });
    }
    flyTo(y) {
        const element = this.viewDrawer.nativeElement;
        // The logic of less and greater may seem reversed, but that's because the drawer position
        // is being translated with a negative y value. The lower the y value, the higher on screen
        // the drawer is positioned. 
        // drawerMax is calculated dynamically because the internal content of drawer can change size at any time.
        const drawerMax = -this.viewDrawer.nativeElement.offsetHeight;
        if (y < drawerMax) {
            y = drawerMax;
        }
        if (y > this.breakpoints.minimumDrawer.point) {
            y = this.breakpoints.minimumDrawer.point;
        }
        const transform = `translate3d(0, ${y}px, 0)`;
        this.renderer.setStyle(element, 'transform', transform);
        // Hide pagination when not at default position
        this.ghostPagination = (y !== this.breakpoints.minimumDrawer.point);
        // Check position
        this.isDrawerAtMinimum();
        this.offsetYChange.emit(y);
    }
    getDrawerY() {
        if (this.viewDrawer) {
            const transform = this.viewService.getComputedTransform(this.viewDrawer.nativeElement);
            return transform ? transform.y : null;
        }
        else {
            return null;
        }
    }
    isDrawerAtMinimum() {
        const y = this.getDrawerY();
        this.atMinimum = (y === this.breakpoints.minimumDrawer.point);
        this.changeDetectorRef.markForCheck();
    }
}
__decorate([
    Select(ViewState.getCanTouch),
    __metadata("design:type", Observable)
], ViewDrawerComponent.prototype, "canTouch$", void 0);
__decorate([
    Select(ViewState.getLayout),
    __metadata("design:type", Observable)
], ViewDrawerComponent.prototype, "layout$", void 0);
__decorate([
    Select(StoryChaptersState.getSelectedChapterIndex),
    __metadata("design:type", Observable)
], ViewDrawerComponent.prototype, "chapterIndex$", void 0);
__decorate([
    Select(StoryChaptersState.getChapterCount),
    __metadata("design:type", Observable)
], ViewDrawerComponent.prototype, "chapterCount$", void 0);
